<template>
  <v-dialog v-model="showDialog" max-width="700px" @update:model-value="onDialogClose">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>MSISDN Details</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-if="!loading && msisdnDetails">
          <!-- MSISDN Info -->
          <v-row>
            <v-col cols="12">
              <h3 class="mb-2">{{ msisdnDetails.msisdn }}</h3>
              <v-chip :color="getStatusColor(msisdnDetails.status_name)" size="small" class="mr-2">
                {{ msisdnDetails.status_name }}
              </v-chip>
              <v-chip v-if="msisdnDetails.msisdn_class_name" variant="outlined" size="small">
                {{ msisdnDetails.msisdn_class_name }}
              </v-chip>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Details Grid -->
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Client:</span>
                <span class="detail-value">{{ msisdnDetails.client_name || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Service:</span>
                <span class="detail-value">{{ msisdnDetails.service_public_name || msisdnDetails.service_name || '-' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Start Date:</span>
                <span class="detail-value">{{ formatDate(msisdnDetails.start_date) }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Expiration Date:</span>
                <span class="detail-value" :class="isExpiringSoon(msisdnDetails.expiration_date) ? 'text-red' : ''">
                  {{ formatDate(msisdnDetails.expiration_date) }}
                </span>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- SIM Info -->
          <h4 class="mb-2">SIM Information</h4>
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">SIM ID:</span>
                <span class="detail-value">
                  <a v-if="msisdnDetails.sim_id" href="#" @click.prevent="goToSim(msisdnDetails.sim_id)" class="text-primary">
                    {{ msisdnDetails.sim_id }}
                    <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
                  </a>
                  <span v-else>-</span>
                </span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">MSISDN Status:</span>
                <span class="detail-value">{{ msisdnDetails.msisdn_status_name || '-' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="msisdnDetails.has_imsi">
            <v-col cols="12">
              <div class="detail-item">
                <span class="detail-label">IMSI:</span>
                <span class="detail-value">{{ msisdnDetails.imsi_number || '-' }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- Groups -->
          <div v-if="msisdnDetails.groups && msisdnDetails.groups.length > 0" class="mt-3">
            <v-divider class="my-3"></v-divider>
            <h4 class="mb-2">Groups</h4>
            <v-chip
              v-for="group in msisdnDetails.groups"
              :key="group.id"
              size="small"
              variant="outlined"
              class="mr-1 mb-1"
            >
              {{ group.name }}
            </v-chip>
          </div>

          <!-- Service Description -->
          <div v-if="msisdnDetails.service_description" class="mt-3">
            <v-divider class="my-3"></v-divider>
            <h4 class="mb-2">Description</h4>
            <p>{{ msisdnDetails.service_description }}</p>
          </div>

          <!-- Related Client Services -->
          <v-divider class="my-3"></v-divider>
          <h4 class="mb-2">Services on this MSISDN</h4>
          <v-table v-if="relatedClientServices && relatedClientServices.length > 0" density="compact" class="mb-3">
            <thead>
              <tr>
                <th class="text-left">Service</th>
                <th class="text-left">Status</th>
                <th class="text-left">Expiration</th>
                <th class="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cs in relatedClientServices" :key="cs.client_service_id">
                <td>{{ cs.service_name }}</td>
                <td>
                  <v-chip :color="getCsStatusColor(cs.cs_status)" size="x-small">
                    {{ cs.cs_status }}
                  </v-chip>
                </td>
                <td>{{ formatDate(cs.expiration_date) }}</td>
                <td>
                  <v-btn
                    variant="text"
                    size="x-small"
                    color="primary"
                    @click="goToClientService(cs.client_service_id)"
                  >
                    View
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-else class="text-grey text-caption">No services found for this MSISDN</div>
        </div>

        <v-alert v-if="!loading && errorMessage" type="error" density="compact">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="updateInfoMessage" :type="updateInfoSuccess ? 'success' : 'error'" density="compact" class="mt-3">
          {{ updateInfoMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn
          v-if="msisdnDetails && msisdnDetails.sim_id"
          color="primary"
          variant="outlined"
          :loading="updatingInfo"
          @click="updateInfo"
        >
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Update Info
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" color="red" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getMsisdnDetails } from "@/composables/useGetAllocatedMsisdnsAPI";
import { refreshSim } from "@/composables/useGetSimsAPI";
import store from "@/store";

export default defineComponent({
  name: 'MsisdnDetailsPopup',
  props: {
    simId: {
      type: [Number, String],
      default: null
    },
    msisdnId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['close'],
  data() {
    return {
      showDialog: true,
      loading: false,
      msisdnDetails: null,
      relatedClientServices: [],
      errorMessage: null,
      updatingInfo: false,
      updateInfoMessage: null,
      updateInfoSuccess: false
    }
  },
  methods: {
    async loadDetails() {
      if (!this.simId && !this.msisdnId) {
        this.errorMessage = "No SIM ID or MSISDN ID provided"
        return
      }

      this.loading = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken
        const response = await getMsisdnDetails(token, this.simId, this.msisdnId)

        if (!response.error) {
          this.msisdnDetails = response.details
          this.relatedClientServices = response.related_client_services || []
          log("Loaded MSISDN details:")
          log(this.msisdnDetails)
          log("Related client services:")
          log(this.relatedClientServices)
        } else {
          this.errorMessage = response.message || "Error loading details"
        }
      } catch (err) {
        log("Exception loading MSISDN details:")
        log(err)
        this.errorMessage = "Error loading details"
      } finally {
        this.loading = false
      }
    },

    async updateInfo() {
      if (!this.msisdnDetails || !this.msisdnDetails.sim_id) {
        this.updateInfoMessage = "No SIM ID available"
        this.updateInfoSuccess = false
        return
      }

      this.updatingInfo = true
      this.updateInfoMessage = null

      try {
        const token = store.getters.getToken
        const response = await refreshSim(token, this.msisdnDetails.sim_id)

        if (!response.error) {
          this.updateInfoMessage = response.message || "Info updated successfully"
          this.updateInfoSuccess = true
          // Reload the details to show updated info
          await this.loadDetails()
        } else {
          this.updateInfoMessage = response.message || "Error updating info"
          this.updateInfoSuccess = false
        }
      } catch (err) {
        log("Exception updating SIM info:")
        log(err)
        this.updateInfoMessage = "Error updating info"
        this.updateInfoSuccess = false
      } finally {
        this.updatingInfo = false
      }
    },

    closeDialog() {
      this.showDialog = false
      this.$emit('close')
    },

    onDialogClose(value) {
      if (!value) {
        this.$emit('close')
      }
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending_payment': 'info',
        'expired': 'warning',
        'suspended': 'orange',
        'terminated': 'error'
      }
      return colors[status] || 'grey'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    isExpiringSoon(dateString) {
      if (!dateString) return false
      const expirationDate = new Date(dateString)
      const now = new Date()
      const daysUntilExpiration = (expirationDate - now) / (1000 * 60 * 60 * 24)
      return daysUntilExpiration <= 7 && daysUntilExpiration > 0
    },

    getCsStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending_payment': 'info',
        'expired': 'warning',
        'suspended': 'orange',
        'terminated': 'error',
        'refunded': 'grey'
      }
      return colors[status] || 'grey'
    },

    goToSim(simId) {
      this.$router.push({ name: 'sims', query: { search: simId.toString() } })
      this.closeDialog()
    },

    goToClientService(csId) {
      this.$router.push({ name: 'clientServices', query: { cs_id: csId } })
      this.closeDialog()
    }
  },

  mounted() {
    this.loadDetails()
  }
})
</script>

<style scoped>
.detail-item {
  margin-bottom: 8px;
}

.detail-label {
  font-weight: 500;
  color: #666;
  display: block;
  font-size: 0.85rem;
}

.detail-value {
  font-size: 0.95rem;
}
</style>
